import React, { useCallback, useState } from "react";
import * as Mui from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { SettingsSection } from "component/shared/settings-page";
import { User } from "model/user";
import { BillingResource } from "api/resource/billing";
import { BillingPaymentType } from "type/billing-payment-type";
import { fetchBilling } from "redux/slice/billing";
import { useAppThunkDispatch } from "redux/store";
import { CARD_ERROR_MESSAGES, CARD_SAVED_MESSAGE } from "./card-errors";
import { CardForm, CardFormHandle, CardFormState } from "./card-form";
import { useBillingStyles } from "./styles";

interface Toast {
	open: boolean;
	message: string;
	severity: "success" | "error";
}

interface Props {
	user: User;
	summary: BillingResource;
	formRef: React.Ref<CardFormHandle>;
	onFormStateChange: (state: CardFormState | null) => void;
}

export const PaymentMethod: React.FC<Props> = ({ user, summary, formRef, onFormStateChange }) => {
	const { classes } = useBillingStyles();
	const dispatch = useAppThunkDispatch();
	const [loadFailed, setLoadFailed] = useState(false);
	const [toast, setToast] = useState<Toast>({ open: false, message: "", severity: "success" });
	const publicKey = summary.recurlyPublicKey;
	const card = summary.card;

	const showToast = useCallback((message: string, severity: Toast["severity"]) => {
		setToast({ open: true, message, severity });
	}, []);

	const handleSaved = useCallback(() => {
		showToast(CARD_SAVED_MESSAGE, "success");
		dispatch(fetchBilling());
	}, [dispatch, showToast]);

	const handleError = useCallback((message: string) => showToast(message, "error"), [showToast]);

	const handleLoadError = useCallback(() => setLoadFailed(true), []);

	const paymentType = card?.paymentType ?? undefined;
	const otherType = paymentType && paymentType !== BillingPaymentType.CARD.value
		? BillingPaymentType.getByValue(paymentType)?.label ?? paymentType
		: undefined;

	let body: React.ReactNode;
	if (!summary.recurlyAvailable || loadFailed || !publicKey) {
		body = (
			<Mui.Alert severity="warning">
				The payment system is temporarily unavailable. Please try again later.
			</Mui.Alert>
		);
	} else {
		body = (
			<>
				{otherType && (
					<Mui.Typography className={classes.paymentOnFile}>
						Payment method on file: {otherType}. Enter a card below to replace it.
					</Mui.Typography>
				)}
				<CardForm
					ref={formRef}
					publicKey={publicKey}
					user={user}
					card={card}
					onSaved={handleSaved}
					onError={handleError}
					onLoadError={handleLoadError}
					onStateChange={onFormStateChange}
				/>
			</>
		);
	}

	return (
		<SettingsSection title="Payment Method">
			{body}
			<Mui.Snackbar
				open={toast.open}
				autoHideDuration={6000}
				onClose={() => setToast((prev) => ({ ...prev, open: false }))}
			>
				<MuiAlert severity={toast.severity} onClose={() => setToast((prev) => ({ ...prev, open: false }))}>
					{toast.message}
				</MuiAlert>
			</Mui.Snackbar>
		</SettingsSection>
	);
};
